import type { CaveSdf, V3 } from './sdf';

/**
 * A cube of space to mesh: grid point (i, j, k) sits at origin + (i, j, k)·cell, and the
 * chunk owns `n` cells along each axis. Neighbouring chunks share grid points exactly
 * (their origins differ by n·cell), so their meshes meet without cracks.
 */
export interface ChunkSpec {
  origin: V3;
  cell: number;
  n: number;
}

/**
 * A gate: the cave's tube runs a little way back into the entrance behind it (for a clean
 * joint), and the mesh is cut there, so it doesn't close the way out. Walls on the far side
 * of the plane through p (normal `n`, pointing into the cave), less than `depth` behind it
 * and within `radius` of the gate's axis (the entrance's tube, where it takes over) are
 * dropped. Nothing beside the entrance is: a passage that swings back past the gate keeps
 * its walls.
 */
export interface Clip {
  p: V3;
  n: V3;
  radius: number;
  depth: number;
}

export interface ChunkMesh {
  /** Cave-local positions. */
  positions: Float32Array;
  /** Unit normals, pointing into the air. */
  normals: Float32Array;
  /** The walls' look (tint rgb, wetness) per vertex. */
  looks: Float32Array;
  indices: Uint32Array;
  /** Added by decorateChunk: glowing props (GLOW_STRIDE numbers each), and solid hulls for them. */
  glow?: Float32Array;
  hullPositions?: Float32Array;
  hullIndices?: Uint32Array;
}

/** Coarse lattice step (grid points), and a bound on how fast the field changes (walls' bumps included). */
const COARSE = 4;
const LIPSCHITZ = 2.5;

// The 12 cube edges as pairs of corner indices; corner c has offset (c&1, c>>1&1, c>>2&1).
const EDGES = [
  [0, 1],
  [2, 3],
  [4, 5],
  [6, 7],
  [0, 2],
  [1, 3],
  [4, 6],
  [5, 7],
  [0, 4],
  [1, 5],
  [2, 6],
  [3, 7],
];

/**
 * Surface nets: one vertex per cell the wall passes through (the average of where it
 * crosses the cell's edges), and a quad across every grid edge the wall cuts, joining the
 * four cells around it. Smooth, with no marching-cubes tables, and it follows the field's
 * sign exactly, so a closed cave gives a closed mesh.
 *
 * The chunk samples (n + 2)³ grid points and makes vertices for (n + 1)³ cells, one layer
 * into its neighbour, and emits only the quads it owns: for an edge along axis a from grid
 * point g, g_a in [0, n) and the other two coordinates in [1, n]. Every edge of the whole
 * grid then belongs to exactly one chunk. Returns null where the chunk has no wall.
 */
export function meshChunk(
  sdf: CaveSdf,
  spec: ChunkSpec,
  cands?: number[],
  clips: Clip[] = [],
): ChunkMesh | null {
  const { origin, cell, n } = spec;
  const P = n + 2; // grid points per axis
  const C = n + 1; // cells per axis
  const box = {
    min: origin,
    max: [origin[0] + (n + 1) * cell, origin[1] + (n + 1) * cell, origin[2] + (n + 1) * cell] as V3,
  };
  const use = cands ?? sdf.candidates(box);
  if (use.length === 0) return null;

  // 1. The field at every grid point. First on a coarse lattice (every COARSE points, on a
  // grid shared by all chunks); then exactly only in coarse cells a wall could be in. A cell
  // whose corners all lie on one side, further from any wall than the field can change
  // across it, has no wall inside: its points just take the corners' blend, the same in
  // every chunk that shares them.
  const Q = Math.ceil((P - 1) / COARSE) + 1; // coarse points per axis
  const coarse = new Float32Array(Q * Q * Q);
  for (let k = 0; k < Q; k++) {
    for (let j = 0; j < Q; j++) {
      for (let i = 0; i < Q; i++) {
        coarse[(k * Q + j) * Q + i] = sdf.distance(
          origin[0] + i * COARSE * cell,
          origin[1] + j * COARSE * cell,
          origin[2] + k * COARSE * cell,
          use,
        );
      }
    }
  }
  const safe = LIPSCHITZ * COARSE * cell * 0.8661; // half the coarse cell's diagonal, times the slope bound
  const clearCell = new Uint8Array((Q - 1) * (Q - 1) * (Q - 1));
  for (let k = 0; k < Q - 1; k++) {
    for (let j = 0; j < Q - 1; j++) {
      for (let i = 0; i < Q - 1; i++) {
        let lo = Number.POSITIVE_INFINITY;
        let pos = 0;
        for (let c = 0; c < 8; c++) {
          const v = coarse[((k + ((c >> 2) & 1)) * Q + (j + ((c >> 1) & 1))) * Q + (i + (c & 1))];
          lo = Math.min(lo, Math.abs(v));
          if (v >= 0) pos++;
        }
        if ((pos === 0 || pos === 8) && lo > safe) clearCell[(k * (Q - 1) + j) * (Q - 1) + i] = 1;
      }
    }
  }
  const vals = new Float32Array(P * P * P);
  let neg = 0;
  for (let k = 0; k < P; k++) {
    const z = origin[2] + k * cell;
    const ck = Math.min(Math.floor(k / COARSE), Q - 2);
    const fk = k / COARSE - ck;
    for (let j = 0; j < P; j++) {
      const y = origin[1] + j * cell;
      const cj = Math.min(Math.floor(j / COARSE), Q - 2);
      const fj = j / COARSE - cj;
      const row = (k * P + j) * P;
      for (let i = 0; i < P; i++) {
        const ci = Math.min(Math.floor(i / COARSE), Q - 2);
        let v: number;
        if (clearCell[(ck * (Q - 1) + cj) * (Q - 1) + ci]) {
          const fi = i / COARSE - ci;
          const at = (di: number, dj: number, dk: number) =>
            coarse[((ck + dk) * Q + (cj + dj)) * Q + (ci + di)];
          const x00 = at(0, 0, 0) + (at(1, 0, 0) - at(0, 0, 0)) * fi;
          const x10 = at(0, 1, 0) + (at(1, 1, 0) - at(0, 1, 0)) * fi;
          const x01 = at(0, 0, 1) + (at(1, 0, 1) - at(0, 0, 1)) * fi;
          const x11 = at(0, 1, 1) + (at(1, 1, 1) - at(0, 1, 1)) * fi;
          const y0 = x00 + (x10 - x00) * fj;
          const y1 = x01 + (x11 - x01) * fj;
          v = y0 + (y1 - y0) * fk;
        } else {
          v = sdf.distance(origin[0] + i * cell, y, z, use);
        }
        vals[row + i] = v;
        if (v < 0) neg++;
      }
    }
  }
  if (neg === 0 || neg === vals.length) return null;

  // 2. A vertex in every cell the wall crosses.
  const cellVert = new Int32Array(C * C * C).fill(-1);
  const pos: number[] = [];
  const corner = new Float32Array(8);
  for (let k = 0; k < C; k++) {
    for (let j = 0; j < C; j++) {
      for (let i = 0; i < C; i++) {
        let mask = 0;
        for (let c = 0; c < 8; c++) {
          const v = vals[((k + ((c >> 2) & 1)) * P + (j + ((c >> 1) & 1))) * P + (i + (c & 1))];
          corner[c] = v;
          if (v < 0) mask |= 1 << c;
        }
        if (mask === 0 || mask === 255) continue;
        let sx = 0;
        let sy = 0;
        let sz = 0;
        let count = 0;
        for (const [a, b] of EDGES) {
          const va = corner[a];
          const vb = corner[b];
          if (va < 0 === vb < 0) continue;
          const t = va / (va - vb);
          sx += (a & 1) + ((b & 1) - (a & 1)) * t;
          sy += ((a >> 1) & 1) + (((b >> 1) & 1) - ((a >> 1) & 1)) * t;
          sz += ((a >> 2) & 1) + (((b >> 2) & 1) - ((a >> 2) & 1)) * t;
          count++;
        }
        cellVert[(k * C + j) * C + i] = pos.length / 3;
        pos.push(
          origin[0] + (i + sx / count) * cell,
          origin[1] + (j + sy / count) * cell,
          origin[2] + (k + sz / count) * cell,
        );
      }
    }
  }

  // 3. A quad across every owned grid edge the wall cuts, facing the air.
  const idx: number[] = [];
  const cellAt = (i: number, j: number, k: number) => cellVert[(k * C + j) * C + i];
  const behindGate = (a: number, b: number, c: number, d: number) => {
    if (clips.length === 0) return false;
    const x = (pos[a * 3] + pos[b * 3] + pos[c * 3] + pos[d * 3]) / 4;
    const y = (pos[a * 3 + 1] + pos[b * 3 + 1] + pos[c * 3 + 1] + pos[d * 3 + 1]) / 4;
    const z = (pos[a * 3 + 2] + pos[b * 3 + 2] + pos[c * 3 + 2] + pos[d * 3 + 2]) / 4;
    for (const g of clips) {
      const dx = x - g.p[0];
      const dy = y - g.p[1];
      const dz = z - g.p[2];
      const along = dx * g.n[0] + dy * g.n[1] + dz * g.n[2];
      if (along >= 0 || along <= -g.depth) continue;
      if (dx * dx + dy * dy + dz * dz - along * along < g.radius * g.radius) return true;
    }
    return false;
  };
  const quad = (a: number, b: number, c: number, d: number, flip: boolean) => {
    if (a < 0 || b < 0 || c < 0 || d < 0) return;
    if (behindGate(a, b, c, d)) return;
    if (flip) idx.push(a, d, c, a, c, b);
    else idx.push(a, b, c, a, c, d);
  };
  for (let k = 0; k <= n; k++) {
    for (let j = 0; j <= n; j++) {
      for (let i = 0; i <= n; i++) {
        const v0 = vals[(k * P + j) * P + i];
        const air0 = v0 < 0;
        // Along x: cells around the edge vary in (y, z).
        if (i < n && j >= 1 && k >= 1 && air0 !== vals[(k * P + j) * P + i + 1] < 0) {
          quad(cellAt(i, j - 1, k - 1), cellAt(i, j, k - 1), cellAt(i, j, k), cellAt(i, j - 1, k), air0);
        }
        // Along y: cells vary in (z, x).
        if (j < n && k >= 1 && i >= 1 && air0 !== vals[(k * P + j + 1) * P + i] < 0) {
          quad(cellAt(i - 1, j, k - 1), cellAt(i - 1, j, k), cellAt(i, j, k), cellAt(i, j, k - 1), air0);
        }
        // Along z: cells vary in (x, y).
        if (k < n && i >= 1 && j >= 1 && air0 !== vals[((k + 1) * P + j) * P + i] < 0) {
          quad(cellAt(i - 1, j - 1, k), cellAt(i, j - 1, k), cellAt(i, j, k), cellAt(i - 1, j, k), air0);
        }
      }
    }
  }
  if (idx.length === 0) return null;

  // 4. Normals from the field's gradient (into the air), and the walls' look.
  const count = pos.length / 3;
  const positions = new Float32Array(pos);
  const normals = new Float32Array(count * 3);
  const looks = new Float32Array(count * 4);
  const e = cell * 0.5;
  for (let v = 0; v < count; v++) {
    const x = pos[v * 3];
    const y = pos[v * 3 + 1];
    const z = pos[v * 3 + 2];
    sdf.distance(x, y, z, use, looks, v * 4);
    // Gradient from a tetrahedron of samples (4 evaluations, not 6).
    const f1 = sdf.distance(x + e, y - e, z - e, use);
    const f2 = sdf.distance(x - e, y - e, z + e, use);
    const f3 = sdf.distance(x - e, y + e, z - e, use);
    const f4 = sdf.distance(x + e, y + e, z + e, use);
    const gx = f1 - f2 - f3 + f4;
    const gy = -f1 - f2 + f3 + f4;
    const gz = -f1 + f2 - f3 + f4;
    const l = Math.hypot(gx, gy, gz) || 1;
    normals[v * 3] = -gx / l;
    normals[v * 3 + 1] = -gy / l;
    normals[v * 3 + 2] = -gz / l;
  }
  return { positions, normals, looks, indices: new Uint32Array(idx) };
}

/** Chunks of `n` cells covering `box`, on a grid aligned to the cave-local origin. */
export function chunkGrid(box: { min: V3; max: V3 }, cell: number, n: number): ChunkSpec[] {
  const size = n * cell;
  const lo = box.min.map((v) => Math.floor(v / size)) as V3;
  const hi = box.max.map((v) => Math.floor(v / size)) as V3;
  const out: ChunkSpec[] = [];
  for (let k = lo[2]; k <= hi[2]; k++) {
    for (let j = lo[1]; j <= hi[1]; j++) {
      for (let i = lo[0]; i <= hi[0]; i++) out.push({ origin: [i * size, j * size, k * size], cell, n });
    }
  }
  return out;
}
